import { useState, useEffect, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Loader2, Save, RotateCcw, Settings, Truck, MapPin, Package,
  DollarSign, Clock, AlertTriangle,
} from "lucide-react";
import { Section, FieldRow } from "./pricing/PricingComponents";

const AdminPricingRules = () => {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/pricing-rules");
      setRules(response.data);
      setDirty(false);
    } catch {
      toast.error("Failed to load pricing rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const updateField = (path, value) => {
    setRules((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { rule_id, version, updated_at, ...updates } = rules;
      await api.put("/admin/pricing-rules", updates);
      toast.success("Pricing rules saved");
      setDirty(false);
      fetchRules();
    } catch (error) {
      toast.error("Failed to save pricing rules");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all pricing rules to factory defaults? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.post("/admin/pricing-rules/reset");
      toast.success("Pricing rules reset to defaults");
      fetchRules();
    } catch (error) {
      toast.error("Failed to reset pricing rules");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Pricing Rules">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
        </div>
      </AdminLayout>
    );
  }

  if (!rules) {
    return (
      <AdminLayout title="Pricing Rules">
        <div className="p-8 text-center text-slate-500">Failed to load pricing rules</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Pricing Rules">
      {/* Sticky Action Bar */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-[#FF5A1F]" />
          <div>
            <span className="font-semibold text-[#0F172A]">Pricing Configuration</span>
            <span className="text-xs text-slate-500 ml-2">v{rules.version || 1}</span>
          </div>
          {dirty && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium" data-testid="unsaved-badge">
              Unsaved changes
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={saving}
            data-testid="reset-rules-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="btn-primary"
            data-testid="save-rules-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Margin & Base Charges */}
        <Section title="Margin & Base Charges" icon={DollarSign} defaultOpen={true}>
          <FieldRow label="Business Margin %" sublabel="Applied on top of internal cost">
            <Input
              type="number"
              step="0.1"
              value={rules.margin_percent}
              onChange={(e) => updateField("margin_percent", parseFloat(e.target.value) || 0)}
              data-testid="margin-percent-input"
            />
          </FieldRow>
          <FieldRow label="Base Move Charge" sublabel="Internal cost base fee">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.base_move_charge}
                onChange={(e) => updateField("base_move_charge", parseFloat(e.target.value) || 0)}
                data-testid="base-move-charge-input"
              />
            </div>
          </FieldRow>
          <FieldRow label="Minimum Charge" sublabel="Minimum customer-facing price">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.min_charge}
                onChange={(e) => updateField("min_charge", parseFloat(e.target.value) || 0)}
                data-testid="min-charge-input"
              />
            </div>
          </FieldRow>
          <FieldRow label="Deposit %" sublabel="Deposit percentage of total">
            <Input
              type="number"
              step="1"
              value={rules.deposit_percent}
              onChange={(e) => updateField("deposit_percent", parseFloat(e.target.value) || 0)}
              data-testid="deposit-percent-input"
            />
          </FieldRow>
        </Section>

        {/* Labour Costs */}
        <Section title="Labour Costs" icon={Clock}>
          <FieldRow label="Hourly Rate (per mover)" sublabel="Internal cost per mover per hour">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.labour_rate_per_hour}
                onChange={(e) => updateField("labour_rate_per_hour", parseFloat(e.target.value) || 0)}
                data-testid="labour-rate-input"
              />
            </div>
          </FieldRow>
          <FieldRow label="Minimum Movers">
            <Input
              type="number"
              min="1"
              value={rules.min_movers}
              onChange={(e) => updateField("min_movers", parseInt(e.target.value) || 2)}
              data-testid="min-movers-input"
            />
          </FieldRow>
          <div>
            <Label className="text-sm font-medium text-[#0F172A] mb-2 block">Hours per Bedroom</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(rules.hours_per_bedroom || {}).map(([beds, hours]) => (
                <div key={beds} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-16">{beds === "0" ? "Studio" : `${beds} bed`}</span>
                  <Input
                    type="number"
                    step="0.5"
                    value={hours}
                    onChange={(e) => updateField(`hours_per_bedroom.${beds}`, parseFloat(e.target.value) || 0)}
                    className="h-9"
                    data-testid={`hours-bed-${beds}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Vehicle Costs */}
        <Section title="Vehicle Costs" icon={Truck}>
          <div className="space-y-3">
            {Object.entries(rules.vehicle_costs || {}).map(([key, vehicle]) => (
              <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50">
                <div className="sm:w-32">
                  <span className="text-sm font-medium text-[#0F172A] capitalize">{key.replace("_", " ")}</span>
                  <p className="text-xs text-slate-500">{vehicle.capacity}</p>
                </div>
                <div className="relative flex-1 max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    className="pl-7 h-9"
                    value={vehicle.cost}
                    onChange={(e) => updateField(`vehicle_costs.${key}.cost`, parseFloat(e.target.value) || 0)}
                    data-testid={`vehicle-cost-${key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Distance Bands */}
        <Section title="Distance Bands" icon={MapPin}>
          <div className="space-y-3">
            {Object.entries(rules.distance_bands || {}).map(([key, band]) => (
              <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50">
                <div className="sm:w-40">
                  <span className="text-sm font-medium text-[#0F172A]">{band.label}</span>
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative max-w-[120px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                    <Input
                      type="number"
                      step="1"
                      className="pl-7 h-9"
                      value={band.cost}
                      onChange={(e) => updateField(`distance_bands.${key}.cost`, parseFloat(e.target.value) || 0)}
                      data-testid={`distance-cost-${key}`}
                    />
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap">max {band.max_miles} mi</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Access Adjustments */}
        <Section title="Access Adjustments" icon={Package}>
          <FieldRow label="Stairs (per floor)" sublabel="Cost per floor without lift">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.access_adjustments?.stairs_per_floor}
                onChange={(e) => updateField("access_adjustments.stairs_per_floor", parseFloat(e.target.value) || 0)}
                data-testid="stairs-cost-input"
              />
            </div>
          </FieldRow>
          <FieldRow label="Long Carry" sublabel="Parking >20m from door">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.access_adjustments?.long_carry}
                onChange={(e) => updateField("access_adjustments.long_carry", parseFloat(e.target.value) || 0)}
                data-testid="long-carry-cost-input"
              />
            </div>
          </FieldRow>
          <FieldRow label="Narrow Access" sublabel="Difficult access surcharge">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.access_adjustments?.narrow_access}
                onChange={(e) => updateField("access_adjustments.narrow_access", parseFloat(e.target.value) || 0)}
                data-testid="narrow-access-cost-input"
              />
            </div>
          </FieldRow>
        </Section>

        {/* Add-on Services */}
        <Section title="Add-on Services" icon={Package}>
          <div>
            <Label className="text-sm font-medium text-[#0F172A] mb-2 block">Packing Service</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(rules.packing_service || {}).map(([key, pack]) => (
                <div key={key} className="flex items-center gap-2 p-3 bg-slate-50">
                  <div className="flex-1">
                    <span className="text-xs text-slate-500">{pack.label}</span>
                  </div>
                  <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      className="pl-7 h-9"
                      value={pack.cost}
                      onChange={(e) => updateField(`packing_service.${key}.cost`, parseFloat(e.target.value) || 0)}
                      data-testid={`packing-cost-${key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <FieldRow label="Dismantling Service" sublabel={rules.dismantling_service?.label}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.dismantling_service?.cost}
                onChange={(e) => updateField("dismantling_service.cost", parseFloat(e.target.value) || 0)}
                data-testid="dismantling-cost-input"
              />
            </div>
          </FieldRow>
          <FieldRow label="Storage (per week)" sublabel={rules.storage_service?.label}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                className="pl-7"
                value={rules.storage_service?.cost_per_week}
                onChange={(e) => updateField("storage_service.cost_per_week", parseFloat(e.target.value) || 0)}
                data-testid="storage-cost-input"
              />
            </div>
          </FieldRow>
        </Section>

        {/* Specialist Items */}
        <Section title="Specialist Items" icon={AlertTriangle}>
          <div className="space-y-3">
            {Object.entries(rules.specialist_items || {}).map(([key, item]) => (
              <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50">
                <div className="sm:w-44">
                  <span className="text-sm font-medium text-[#0F172A]">{item.label}</span>
                </div>
                <div className="relative flex-1 max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    className="pl-7 h-9"
                    value={item.cost}
                    onChange={(e) => updateField(`specialist_items.${key}.cost`, parseFloat(e.target.value) || 0)}
                    data-testid={`specialist-cost-${key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Surcharges */}
        <Section title="Time Surcharges" icon={Clock}>
          <FieldRow label="Weekend Surcharge %" sublabel="Extra charge for Saturday/Sunday moves">
            <Input
              type="number"
              step="1"
              value={rules.weekend_surcharge_percent}
              onChange={(e) => updateField("weekend_surcharge_percent", parseFloat(e.target.value) || 0)}
              data-testid="weekend-surcharge-input"
            />
          </FieldRow>
          <FieldRow label="Urgent Surcharge %" sublabel="Extra charge for moves within 7 days">
            <Input
              type="number"
              step="1"
              value={rules.urgent_surcharge_percent}
              onChange={(e) => updateField("urgent_surcharge_percent", parseFloat(e.target.value) || 0)}
              data-testid="urgent-surcharge-input"
            />
          </FieldRow>
        </Section>

        {/* Instant Quote Eligibility */}
        <Section title="Instant Quote Eligibility" icon={Settings}>
          <FieldRow label="Max Bedrooms" sublabel="Above this, survey required">
            <Input
              type="number"
              min="0"
              value={rules.instant_quote_rules?.max_bedrooms}
              onChange={(e) => updateField("instant_quote_rules.max_bedrooms", parseInt(e.target.value) || 0)}
              data-testid="iqr-max-bedrooms"
            />
          </FieldRow>
          <FieldRow label="Max Floor (no lift)" sublabel="Above this floor, survey required">
            <Input
              type="number"
              min="0"
              value={rules.instant_quote_rules?.max_floor_without_lift}
              onChange={(e) => updateField("instant_quote_rules.max_floor_without_lift", parseInt(e.target.value) || 0)}
              data-testid="iqr-max-floor"
            />
          </FieldRow>
          <FieldRow label="Allow Storage" sublabel="Instant quote for storage moves">
            <Switch
              checked={rules.instant_quote_rules?.allow_storage || false}
              onCheckedChange={(checked) => updateField("instant_quote_rules.allow_storage", checked)}
              data-testid="iqr-allow-storage"
            />
          </FieldRow>
          <FieldRow label="Allow Specialist Items" sublabel="Instant quote for specialist items">
            <Switch
              checked={rules.instant_quote_rules?.allow_specialist_items || false}
              onCheckedChange={(checked) => updateField("instant_quote_rules.allow_specialist_items", checked)}
              data-testid="iqr-allow-specialist"
            />
          </FieldRow>
        </Section>
      </div>
    </AdminLayout>
  );
};

export default AdminPricingRules;
